/**
 * Heuristic categorization of payload fields by name.
 * Used to group large flat payload schemas (like Restrictions) into
 * collapsible sections for better usability.
 */
import {
  Share2,
  UserCircle,
  AppWindow,
  Music,
  ShieldCheck,
  Wifi,
  Settings2,
  MoreHorizontal,
  type LucideIcon,
} from "lucide-react";

export type CategoryId =
  | "sharing"
  | "accounts"
  | "apps"
  | "media"
  | "security"
  | "network"
  | "system"
  | "other";

export interface CategoryMeta {
  id: CategoryId;
  label: string;
  order: number;
  icon: LucideIcon;
}

export const CATEGORIES: Record<CategoryId, CategoryMeta> = {
  sharing: { id: "sharing", label: "Sharing & Connectivity", order: 1, icon: Share2 },
  accounts: { id: "accounts", label: "Accounts & iCloud", order: 2, icon: UserCircle },
  apps: { id: "apps", label: "Apps & App Store", order: 3, icon: AppWindow },
  media: { id: "media", label: "Media & Entertainment", order: 4, icon: Music },
  security: { id: "security", label: "Security & Passcode", order: 5, icon: ShieldCheck },
  network: { id: "network", label: "Network", order: 6, icon: Wifi },
  system: { id: "system", label: "System & Device", order: 7, icon: Settings2 },
  other: { id: "other", label: "Other", order: 99, icon: MoreHorizontal },
};

const RULES: Array<{ id: CategoryId; patterns: RegExp[] }> = [
  {
    id: "sharing",
    patterns: [
      /airplay/i, /airprint/i, /airdrop/i, /handoff/i, /sharing/i,
      /bluetooth/i, /continuity/i, /universalclipboard/i, /nearby/i, /share/i,
    ],
  },
  {
    id: "accounts",
    patterns: [
      /account/i, /icloud/i, /appleid/i, /apple_id/i, /findmy/i, /find_my/i,
      /keychain/i, /managedappleaccount/i,
    ],
  },
  {
    id: "apps",
    patterns: [
      /^allowapp/i, /appstore/i, /app_store/i, /appclip/i, /appremoval/i,
      /appinstall/i, /appremoved/i, /^apps?$/i, /inapppurchase/i,
    ],
  },
  {
    id: "media",
    patterns: [
      /game/i, /music/i, /siri/i, /camera/i, /photo/i, /video/i,
      /screenshot/i, /screenrecord/i, /podcast/i, /itunes/i, /radio/i,
      /facetime/i, /imessage/i, /booklore/i, /book/i, /news/i,
    ],
  },
  {
    id: "security",
    patterns: [
      /passcode/i, /password/i, /security/i, /encrypt/i, /touchid/i,
      /faceid/i, /biometric/i, /lock/i, /unlock/i, /firewall/i,
      /gatekeeper/i, /xprotect/i, /privacy/i, /authenticat/i, /credential/i,
    ],
  },
  {
    id: "network",
    patterns: [
      /network/i, /vpn/i, /wifi/i, /wi_fi/i, /cellular/i, /hotspot/i,
      /tethering/i, /proxy/i, /dns/i, /ethernet/i, /modem/i,
    ],
  },
  {
    id: "system",
    patterns: [
      /update/i, /upgrade/i, /erase/i, /reset/i, /diagnostic/i,
      /backup/i, /restore/i, /siri/i, /spotlight/i, /restriction/i,
      /managed/i, /supervis/i, /enroll/i, /devicename/i, /wallpaper/i,
    ],
  },
];

export function categorizeField(key: string): CategoryId {
  for (const { id, patterns } of RULES) {
    if (patterns.some((p) => p.test(key))) return id;
  }
  return "other";
}

export function getCategoryMeta(id: CategoryId): CategoryMeta {
  return CATEGORIES[id];
}

export function sortedCategoryIds(ids: CategoryId[]): CategoryId[] {
  return [...new Set(ids)].sort((a, b) => CATEGORIES[a].order - CATEGORIES[b].order);
}
